import { jsxs, jsx, Fragment } from "react/jsx-runtime";
function TriangleChart({
  scores,
  thresholds,
  title,
  maxScore = 100,
  height = 260,
  width = 200,
  showArrows = false
}) {
  const { firstThird, secondThird } = thresholds;
  const padding = { top: 24, bottom: 16, left: 16, right: 16 };
  const triTop = { x: width / 2, y: padding.top };
  const triBottomLeft = { x: padding.left + 10, y: height - padding.bottom };
  const triBottomRight = { x: width - padding.right - 10, y: height - padding.bottom };
  const triBottomCenter = { y: height - padding.bottom };
  const triHeight = triBottomCenter.y - triTop.y;
  const scoreToY = (score) => {
    const ratio = Math.min(score, maxScore) / maxScore;
    return triBottomCenter.y - ratio * triHeight;
  };
  const widthAtY = (y) => {
    const ratio = (triBottomCenter.y - y) / triHeight;
    const baseWidth = triBottomRight.x - triBottomLeft.x;
    return baseWidth * (1 - ratio);
  };
  const firstThirdY = scoreToY(firstThird);
  const secondThirdY = scoreToY(secondThird);
  const getXOffset = (index, total) => {
    if (total === 1) return 0;
    if (total === 2) return index === 0 ? -1 : 1;
    if (total === 3) return index - 1;
    return 0;
  };
  const zoneLabelX = width - padding.right - 4;
  const zoneLabels = [
    { y: (triTop.y + firstThirdY) / 2, text: `${firstThird}~${maxScore}` },
    { y: (firstThirdY + secondThirdY) / 2, text: `${secondThird}~${firstThird}` },
    { y: (secondThirdY + triBottomCenter.y) / 2, text: `0~${secondThird}` }
  ];
  const getDividerEndpoints = (y) => {
    const w = widthAtY(y);
    const cx = width / 2;
    return { x1: cx - w / 2, x2: cx + w / 2, y };
  };
  const divider1 = getDividerEndpoints(firstThirdY);
  const divider2 = getDividerEndpoints(secondThirdY);
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center", children: [
    title && /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold text-gray-700 mb-2", children: title }),
    /* @__PURE__ */ jsxs("svg", { width, height, viewBox: `0 0 ${width} ${height}`, children: [
      /* @__PURE__ */ jsx(
        "polygon",
        {
          points: `${triTop.x},${triTop.y} ${triBottomLeft.x},${triBottomLeft.y} ${triBottomRight.x},${triBottomRight.y}`,
          fill: "#f8f9ff",
          stroke: "#c7d2fe",
          strokeWidth: 1.5
        }
      ),
      /* @__PURE__ */ jsx(
        "line",
        {
          x1: divider1.x1,
          y1: divider1.y,
          x2: divider1.x2,
          y2: divider1.y,
          stroke: "#c7d2fe",
          strokeWidth: 1,
          strokeDasharray: "4,3"
        }
      ),
      /* @__PURE__ */ jsx(
        "line",
        {
          x1: divider2.x1,
          y1: divider2.y,
          x2: divider2.x2,
          y2: divider2.y,
          stroke: "#c7d2fe",
          strokeWidth: 1,
          strokeDasharray: "4,3"
        }
      ),
      zoneLabels.map((zl, i) => /* @__PURE__ */ jsx(
        "text",
        {
          x: zoneLabelX,
          y: zl.y,
          fontSize: 9,
          fill: "#94a3b8",
          textAnchor: "end",
          dominantBaseline: "middle",
          children: zl.text
        },
        i
      )),
      scores.map((score, i) => {
        const y = scoreToY(score.value);
        const xOffset = getXOffset(i, scores.length) * 14;
        const x = width / 2 + xOffset;
        const compY = score.comparisonValue != null ? scoreToY(score.comparisonValue) : null;
        const compX = x;
        return /* @__PURE__ */ jsxs("g", { children: [
          /* @__PURE__ */ jsx("circle", { cx: x, cy: y, r: 6, fill: score.color, opacity: 0.9 }),
          /* @__PURE__ */ jsx(
            "text",
            {
              x,
              y: y - 10,
              fontSize: 10,
              fill: score.color,
              textAnchor: "middle",
              fontWeight: "bold",
              children: Math.round(score.value)
            }
          ),
          compY != null && score.comparisonColor && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("circle", { cx: compX, cy: compY, r: 6, fill: score.comparisonColor, opacity: 0.9 }),
            /* @__PURE__ */ jsx(
              "text",
              {
                x: compX,
                y: compY - 10,
                fontSize: 10,
                fill: score.comparisonColor,
                textAnchor: "middle",
                fontWeight: "bold",
                children: Math.round(score.comparisonValue)
              }
            ),
            showArrows && /* @__PURE__ */ jsx(
              "line",
              {
                x1: x,
                y1: y,
                x2: compX,
                y2: compY,
                stroke: score.comparisonValue > score.value ? "#22c55e" : "#ef4444",
                strokeWidth: 1.5,
                markerEnd: "url(#arrowhead)",
                opacity: 0.7
              }
            )
          ] }),
          /* @__PURE__ */ jsx(
            "text",
            {
              x,
              y: height - 2,
              fontSize: 10,
              fill: "#6b7280",
              textAnchor: "middle",
              children: score.label
            }
          )
        ] }, i);
      }),
      /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx(
        "marker",
        {
          id: "arrowhead",
          markerWidth: "8",
          markerHeight: "6",
          refX: "8",
          refY: "3",
          orient: "auto",
          children: /* @__PURE__ */ jsx("polygon", { points: "0 0, 8 3, 0 6", fill: "#6b7280" })
        }
      ) })
    ] })
  ] });
}
export {
  TriangleChart
};
//# sourceMappingURL=TriangleChart.js.map
